import React, { useState, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import Button from "@mui/material/Button";
import { useSwipeable } from "react-swipeable";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

interface CustomPDFViewerProps {
  file: string;
  numPages: number;
}

const PDFViewer: React.FC<CustomPDFViewerProps> = ({ file }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [viewportWidth, setViewportWidth] = useState<number>(window.innerWidth);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    pdfjs.getDocument(file).promise.then((pdf) => {
      setNumPages(pdf.numPages);
    });
  }, [file]);

  const calculateScale = () => {
    const isMobile = viewportWidth < 768;
    return isMobile ? 0.5 : 1;
  };

  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

  const handleNext = () => {
    if (currentPage < numPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleNext(),
    onSwipedRight: () => handlePrev(),
    trackMouse: true,
  });

  return (
    <div className="pdf-container mx-auto -mt-4" {...swipeHandlers}>
      <Document file={file}>
        {currentPage <= numPages && (
          <Page
            key={`page_${currentPage}`}
            pageNumber={currentPage}
            scale={calculateScale()}
          />
        )}
      </Document>
      {!isSmallScreen && (
        <div className="flex justify-center space-x-4 mt-4">
          <Button
            variant="outlined"
            size="small"
            onClick={handlePrev}
            disabled={currentPage === 1}
          >
            Previous Page
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleNext}
            disabled={currentPage === numPages}
          >
            Next Page
          </Button>
        </div>
      )}
    </div>
  );
};

export default PDFViewer;
